import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * A message as PARAGRAPHS, with parts of it emphasised.
 *
 * ── WHY MESSAGES STOPPED BEING STRINGS ──────────────────────────────────────
 * The billing confirmations were single strings rendered with
 * `whiteSpace: 'pre-line'`. That gets line breaks but nothing else: the lines
 * sit flush against each other with no breathing room, and no part of a line
 * can be weighted. Both downgrade dialogs needed exactly that — spacing between
 * the lines, and the sentence about money picked out — so the messages now
 * carry structure instead of the renderer guessing at it.
 *
 * Shape: an array of paragraphs, each an array of `{ text, strong }` segments.
 * The copy helpers build it; nothing here decides what to emphasise, so the
 * wording and its weighting stay in one place next to each other.
 */
export default function RichMessage({
  paragraphs = [],
  /** Gap between paragraphs. The whole point of the component, so it is named. */
  spacing = 2,
  sx = {},
  ...typographyProps
}) {
  if (!Array.isArray(paragraphs) || paragraphs.length === 0) return null;

  return (
    <>
      {paragraphs.map((segments, i) => {
        const parts = Array.isArray(segments) ? segments : [{ text: String(segments ?? '') }];
        return (
          <Typography
            // Paragraphs are stable for a given message and there is no id to
            // key on; their joined text is what actually distinguishes them.
            key={parts.map((p) => p.text).join('')}
            sx={{ mb: i === paragraphs.length - 1 ? 0 : spacing, ...sx }}
            {...typographyProps}
          >
            {parts.map((part) => (part.strong ? (
              <Box component="span" key={part.text} sx={{ fontWeight: 700 }}>
                {part.text}
              </Box>
            ) : (
              <React.Fragment key={part.text}>{part.text}</React.Fragment>
            )))}
          </Typography>
        );
      })}
    </>
  );
}
